import React, { useState } from 'react';
import { Camera, Plus, Trash2, Link as LinkIcon, Star, Save, X, Settings } from 'lucide-react';
import { doc, collection, setDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { Club, Config } from '../../types';
import { resizeImage } from '../../lib/utils';

interface AdminClubesProps {
  clubs: Club[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
}

export function AdminClubes({ clubs, db, getCollectionPath, config }: AdminClubesProps) {
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [instagram, setInstagram] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Configuration for sponsors carousel
  const [carouselSettings, setCarouselSettings] = useState({
    sponsorsCarouselTitle: config.sponsorsCarouselTitle || 'Partners Oficiales',
    sponsorsCarouselSubtitle: config.sponsorsCarouselSubtitle || 'Sponsor Oficial',
    sponsorsShowSubtitle: config.sponsorsShowSubtitle !== false,
    sponsorsBgColor: config.sponsorsBgColor || '#ffffff',
    sponsorsBgOpacity: config.sponsorsBgOpacity !== undefined ? config.sponsorsBgOpacity : 3,
    sponsorsTitleOpacity: config.sponsorsTitleOpacity !== undefined ? config.sponsorsTitleOpacity : 20,
    sponsorsCarouselSpeed: config.sponsorsCarouselSpeed !== undefined ? config.sponsorsCarouselSpeed : 30,
    sponsorsCardSize: config.sponsorsCardSize || 'md',
    sponsorsTextColor: config.sponsorsTextColor || '#ffffff',
    sponsorsOuterBox: config.sponsorsOuterBox !== false,
    sponsorsInnerBox: config.sponsorsInnerBox !== false,
    sponsorsOuterBoxOpacity: config.sponsorsOuterBoxOpacity !== undefined ? config.sponsorsOuterBoxOpacity : 5,
    sponsorsInnerBoxOpacity: config.sponsorsInnerBoxOpacity !== undefined ? config.sponsorsInnerBoxOpacity : 5,
    sponsorsGlowEffect: config.sponsorsGlowEffect === true,
    sponsorsGlowAmount: config.sponsorsGlowAmount !== undefined ? config.sponsorsGlowAmount : 50,
    sponsorsGlowColor: config.sponsorsGlowColor || config.sponsorsBgColor || '#ffffff',
    sponsorsEdgeFade: config.sponsorsEdgeFade !== false,
    sponsors3DEffect: config.sponsors3DEffect === true,
    sponsorsCardScale: config.sponsorsCardScale !== undefined ? config.sponsorsCardScale : 100,
    sponsors3DIntensity: config.sponsors3DIntensity !== undefined ? config.sponsors3DIntensity : 35,
    sponsorsTextScale: config.sponsorsTextScale !== undefined ? config.sponsorsTextScale : 100,
    sponsorsShowText: config.sponsorsShowText !== false,
    sponsorsGap: config.sponsorsGap !== undefined ? config.sponsorsGap : 32,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [presets, setPresets] = useState<any[]>([]);
  const [showPresetsModal, setShowPresetsModal] = useState(false);
  const [presetNameInput, setPresetNameInput] = useState('');
  const [loadingPresets, setLoadingPresets] = useState(false);

  const loadPresets = async () => {
    try {
      setLoadingPresets(true);
      const q = query(collection(db, getCollectionPath('settings')), where('docType', '==', 'sponsor_preset'));
      const sn = await getDocs(q);
      const loaded = sn.docs.map(d => ({ id: d.id, ...d.data() }));
      setPresets(loaded);
    } catch(err) {
      console.error(err);
    } finally {
      setLoadingPresets(false);
    }
  };

  const handleSavePreset = async () => {
    if (!presetNameInput.trim()) return alert('Debes darle un nombre');
    const presetId = 'sponsor_preset_' + Date.now();
    try {
      await setDoc(doc(db, getCollectionPath('settings'), presetId), {
        ...carouselSettings,
        docType: 'sponsor_preset',
        presetName: presetNameInput,
        createdAt: Date.now()
      });
      setPresetNameInput('');
      loadPresets();
    } catch(err) {
      alert('Error: ' + (err as any).message);
    }
  };

  const handleDeletePreset = async (id: string) => {
    try {
      await deleteDoc(doc(db, getCollectionPath('settings'), id));
      loadPresets();
    } catch(err) {
      alert('Error eliminando');
    }
  };

  const handleApplyPreset = (presetConfig: any) => {
    const { docType, presetName, createdAt, id, ...rest } = presetConfig;
    setCarouselSettings({ ...carouselSettings, ...rest });
    setShowPresetsModal(false);
  };


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsLoading(true);
      resizeImage(
        file,
        (compressed) => {
          setPhotoUrl(compressed);
          setIsLoading(false);
        },
        400,
        400,
        0.9
      );
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    await setDoc(doc(db, getCollectionPath('settings'), 'main'), carouselSettings, { merge: true });
    setSavingSettings(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    setIsLoading(true);
    try {
      let finalPhotoUrl = photoUrl;
      if (finalPhotoUrl.startsWith('data:image/')) {
        const imageRef = ref(storage, `sponsors/sponsor_${Date.now()}.jpg`);
        await uploadString(imageRef, finalPhotoUrl, 'data_url');
        finalPhotoUrl = await getDownloadURL(imageRef);
      }
      
      const clubRef = doc(collection(db, getCollectionPath('clubs')));
      const cId = clubRef.id;
      await setDoc(clubRef, {
        id: cId,
        name,
        link,
        instagram,
        whatsapp,
        photoUrl: finalPhotoUrl,
        type: 'sponsor',
        createdAt: Date.now(),
      });
      setName('');
      setLink('');
      setInstagram('');
      setWhatsapp('');
      setPhotoUrl('');
    } catch (err) {
      console.error("Error creating sponsor:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrar solo los sponsors, ya que los clubes se manejan en otra vista
  const sponsors = clubs.filter(c => c.type === 'sponsor');

  return (
    <div className="space-y-8">
      {/* Configuración Visual del Carrusel */}
      <div className="bg-black/20 p-6 rounded-2xl border border-white/10 space-y-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-black uppercase tracking-wider flex items-center theme-font-secondary">
            <Star className="mr-2 theme-primary-text" /> Personalizar Carrusel
          </h3>
          
          <div className="flex gap-2">
          <button onClick={() => { setShowPresetsModal(true); loadPresets(); }} className="hidden md:flex bg-white/5 border border-white/10 hover:bg-white/10 text-white font-black px-6 py-2 rounded-lg text-xs uppercase tracking-widest transition-colors items-center h-fit">
            Presets
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="hidden md:flex theme-primary-bg text-black font-black px-6 py-2 rounded-lg text-xs uppercase tracking-widest hover:opacity-80 transition-colors items-center h-fit"
          >
            {savingSettings ? (
              'Guardando...'
            ) : (
              <>
                <Save size={16} className="mr-2" /> Guardar Ajustes
              </>
            )}
          </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black opacity-60 uppercase block mb-1">Título de la sección</label>
              <input
                type="text"
                value={carouselSettings.sponsorsCarouselTitle}
                onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsCarouselTitle: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border font-bold text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-black opacity-60 uppercase block mb-1">Subtítulo bajo cada logo</label>
              <input
                type="text"
                value={carouselSettings.sponsorsCarouselSubtitle}
                onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsCarouselSubtitle: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border font-bold text-sm"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer w-fit mt-1">
              <input
                type="checkbox"
                checked={carouselSettings.sponsorsShowSubtitle}
                onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsShowSubtitle: e.target.checked })}
                className="w-4 h-4"
                style={{ accentColor: config.primaryColor || '#eab308' }}
              />
              <span className="text-xs font-bold text-white/80">Mostrar este subtítulo en cada logo</span>
            </label>

            <div className="pt-2">
              <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-2">
                <span>Opacidad del Título</span>
                <span>{carouselSettings.sponsorsTitleOpacity}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={carouselSettings.sponsorsTitleOpacity}
                onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsTitleOpacity: Number(e.target.value) })}
                className="w-full"
                style={{ accentColor: config.primaryColor || '#eab308' }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black opacity-60 uppercase block mb-1">Color de las cajas de los logos</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={carouselSettings.sponsorsBgColor}
                  onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsBgColor: e.target.value })}
                  className="w-10 h-10 rounded border-0 bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={carouselSettings.sponsorsBgColor}
                  onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsBgColor: e.target.value })}
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg p-2.5 text-xs font-mono uppercase focus:theme-primary-border outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-1">
                <span>Velocidad de Carrusel</span>
                <span>{carouselSettings.sponsorsCarouselSpeed}s (Ciclo)</span>
              </label>
              <input
                type="range"
                min="5"
                max="60"
                step="5"
                value={carouselSettings.sponsorsCarouselSpeed}
                onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsCarouselSpeed: Number(e.target.value) })}
                className="w-full"
                style={{ accentColor: config.primaryColor || '#eab308' }}
              />
              <p className="text-[8px] opacity-40 uppercase">Menos es más rápido. El valor es el tiempo en recorrer la lista entera.</p>
            </div>

            <div>
               <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-1">
                 <span>Escala General de las cajas</span>
                 <span>{carouselSettings.sponsorsCardScale}%</span>
               </label>
               <input
                 type="range"
                 min="50"
                 max="200"
                 value={carouselSettings.sponsorsCardScale}
                 onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsCardScale: Number(e.target.value) })}
                 className="w-full mb-4"
                 style={{ accentColor: config.primaryColor || '#eab308' }}
               />
               
               <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-1">
                 <span>Separación entre sponsors</span>
                 <span>{carouselSettings.sponsorsGap}px</span>
               </label>
               <input
                 type="range"
                 min="0"
                 max="100"
                 value={carouselSettings.sponsorsGap}
                 onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsGap: Number(e.target.value) })}
                 className="w-full"
                 style={{ accentColor: config.primaryColor || '#eab308' }}
               />
            </div>

            <div className="pt-2">
              <label className="flex items-center gap-2 cursor-pointer w-fit mb-2">
                <input type="checkbox" checked={carouselSettings.sponsorsOuterBox} onChange={e => setCarouselSettings({...carouselSettings, sponsorsOuterBox: e.target.checked})} className="w-4 h-4" />
                <span className="text-xs font-bold text-white/80">Mostrar Recuadro Externo</span>
              </label>
              {carouselSettings.sponsorsOuterBox && (
                <div className="pl-6 mb-4">
                  <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-2">
                    <span>Opacidad del recuadro externo</span>
                    <span>{carouselSettings.sponsorsOuterBoxOpacity}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={carouselSettings.sponsorsOuterBoxOpacity}
                    onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsOuterBoxOpacity: Number(e.target.value) })}
                    className="w-full"
                    style={{ accentColor: config.primaryColor || '#eab308' }}
                  />
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-white/5">
              <label className="flex items-center gap-2 cursor-pointer w-fit mb-2">
                <input type="checkbox" checked={carouselSettings.sponsorsInnerBox} onChange={e => setCarouselSettings({...carouselSettings, sponsorsInnerBox: e.target.checked})} className="w-4 h-4" />
                <span className="text-xs font-bold text-white/80">Mostrar Recuadro Interno (Logo)</span>
              </label>
              {carouselSettings.sponsorsInnerBox && (
                <div className="pl-6 mb-2">
                  <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-2">
                    <span>Opacidad del recuadro interno</span>
                    <span>{carouselSettings.sponsorsInnerBoxOpacity}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={carouselSettings.sponsorsInnerBoxOpacity}
                    onChange={(e) => setCarouselSettings({ ...carouselSettings, sponsorsInnerBoxOpacity: Number(e.target.value) })}
                    className="w-full"
                    style={{ accentColor: config.primaryColor || '#eab308' }}
                  />
                </div>
              )}
            </div>
            
            {/* NOVEDADES SPONSOR */}
            <div className="pt-4 border-t border-white/10 mt-4 px-1">
               <h4 className="font-bold text-xs theme-primary-text uppercase tracking-widest mb-4">Efectos Avanzados</h4>
               
               <div className="space-y-4">
                 <label className="flex items-center justify-between cursor-pointer">
                   <span className="text-[10px] font-black opacity-80 uppercase text-white">Difuminado en los bordes</span>
                   <input type="checkbox" checked={carouselSettings.sponsorsEdgeFade} onChange={e => setCarouselSettings({...carouselSettings, sponsorsEdgeFade: e.target.checked})} className="w-4 h-4" />
                 </label>
                 
                 <label className="flex items-center justify-between cursor-pointer">
                   <span className="text-[10px] font-black opacity-80 uppercase text-white">Efecto Lupa 3D al centro</span>
                   <input type="checkbox" checked={carouselSettings.sponsors3DEffect} onChange={e => setCarouselSettings({...carouselSettings, sponsors3DEffect: e.target.checked})} className="w-4 h-4" />
                 </label>
                 
                 {carouselSettings.sponsors3DEffect && (
                    <div className="bg-black/20 p-3 rounded-lg border border-white/5 space-y-3">
                       <div>
                         <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-1">
                           <span className="text-white">Intensidad de la Lupa</span>
                           <span className="text-white">{carouselSettings.sponsors3DIntensity}</span>
                         </label>
                         <input type="range" min="0" max="100" value={carouselSettings.sponsors3DIntensity} onChange={e => setCarouselSettings({...carouselSettings, sponsors3DIntensity: Number(e.target.value)})} className="w-full" />
                       </div>
                    </div>
                 )}
                 
                 <label className="flex items-center justify-between cursor-pointer">
                   <span className="text-[10px] font-black opacity-80 uppercase text-white">Efecto Resplandor (Glow)</span>
                   <input type="checkbox" checked={carouselSettings.sponsorsGlowEffect} onChange={e => setCarouselSettings({...carouselSettings, sponsorsGlowEffect: e.target.checked})} className="w-4 h-4" />
                 </label>
                 
                 {carouselSettings.sponsorsGlowEffect && (
                    <div className="bg-black/20 p-3 rounded-lg border border-white/5 space-y-3">
                       <div className="flex gap-2">
                         <input type="color" value={carouselSettings.sponsorsGlowColor} onChange={e => setCarouselSettings({...carouselSettings, sponsorsGlowColor: e.target.value})} className="w-8 h-8 rounded border-0 bg-transparent cursor-pointer" />
                         <input type="text" value={carouselSettings.sponsorsGlowColor} onChange={e => setCarouselSettings({...carouselSettings, sponsorsGlowColor: e.target.value})} className="flex-1 bg-black/40 border border-white/10 rounded p-1 text-[10px] font-mono text-white placeholder-white/30 outline-none" />
                       </div>
                       
                       <div>
                         <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-1">
                           <span className="text-white">Intensidad</span>
                           <span className="text-white">{carouselSettings.sponsorsGlowAmount}px</span>
                         </label>
                         <input type="range" min="0" max="100" value={carouselSettings.sponsorsGlowAmount} onChange={e => setCarouselSettings({...carouselSettings, sponsorsGlowAmount: Number(e.target.value)})} className="w-full" />
                       </div>
                    </div>
                 )}
               </div>
            </div>
            
            <div className="pt-4 border-t border-white/10 mt-4 px-1">
               <h4 className="font-bold text-xs theme-primary-text uppercase tracking-widest mb-4">Estilo de Texto</h4>
               
               <label className="flex items-center gap-2 cursor-pointer w-fit mb-4">
                 <input type="checkbox" checked={carouselSettings.sponsorsShowText} onChange={e => setCarouselSettings({...carouselSettings, sponsorsShowText: e.target.checked})} className="w-4 h-4" />
                 <span className="text-xs font-bold text-white/80">Mostrar nombre del sponsor</span>
               </label>
               
               {carouselSettings.sponsorsShowText && (
                 <>
                   <div className="mb-4">
                     <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-1">
                       <span className="text-white">Escala del Texto</span>
                       <span className="text-white">{carouselSettings.sponsorsTextScale}%</span>
                     </label>
                     <input type="range" min="50" max="250" value={carouselSettings.sponsorsTextScale} onChange={e => setCarouselSettings({...carouselSettings, sponsorsTextScale: Number(e.target.value)})} className="w-full" />
                   </div>

                   <label className="text-[10px] font-black opacity-60 uppercase block mb-1 text-white">Color de Texto</label>
                   <div className="flex gap-2">
                     <input type="color" value={carouselSettings.sponsorsTextColor} onChange={e => setCarouselSettings({...carouselSettings, sponsorsTextColor: e.target.value})} className="w-8 h-8 rounded border-0 bg-transparent cursor-pointer" />
                     <input type="text" value={carouselSettings.sponsorsTextColor} onChange={e => setCarouselSettings({...carouselSettings, sponsorsTextColor: e.target.value})} className="flex-1 bg-black/40 border border-white/10 rounded px-2 text-xs font-mono text-white placeholder-white/30 outline-none" />
                   </div>
                 </>
               )}
            </div>
          </div>
        </div>

        <button onClick={() => { setShowPresetsModal(true); loadPresets(); }} className="w-full mt-4 flex md:hidden bg-white/5 border border-white/10 text-white font-black px-6 py-3 rounded-xl text-sm uppercase tracking-widest items-center justify-center">Mis Presets</button>
        <button
          onClick={handleSaveSettings}
          disabled={savingSettings}
          className="w-full mt-2 flex md:hidden theme-primary-bg text-black font-black px-6 py-3 rounded-xl text-sm uppercase tracking-widest hover:opacity-80 transition-colors items-center justify-center"
        >
          {savingSettings ? (
            'Guardando...'
          ) : (
            <>
              <Save size={18} className="mr-2" /> Guardar Ajustes
            </>
          )}
        </button>
      
      {showPresetsModal && (
        <div className="fixed bottom-0 right-0 z-[100] p-4 md:p-8 flex flex-col items-end pointer-events-none w-full h-full justify-end">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl border border-white/20 rounded-[var(--radius-large)] w-full max-w-sm shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[70vh] pointer-events-auto animate-in slide-in-from-bottom-8 fade-in flex-shrink-0 mb-[160px] md:mb-[120px]">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black leading-none text-white">Presets de Carrusel</h3>
                <p className="text-[10px] opacity-60 mt-1 uppercase tracking-wider text-white">Guardar/Cargar Estilos</p>
              </div>
              <button onClick={() => setShowPresetsModal(false)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors text-white">
                 <X size={16} />
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto flex-1 space-y-6 flex flex-col">
               <div className="bg-black/60 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                   <div className="w-full">
                       <input 
                         type="text" 
                         placeholder="Nombre del Preset (ej: Torneos)"
                         value={presetNameInput}
                         onChange={e => setPresetNameInput(e.target.value)}
                         className="w-full bg-white/5 text-white placeholder-white/30 border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-white/30 outline-none transition-colors"
                       />
                   </div>
                   <button 
                      onClick={handleSavePreset}
                      className="w-full bg-theme-primary text-black font-bold px-4 py-2 rounded-xl hover:opacity-80 transition-opacity text-sm flex items-center justify-center"
                    >
                      <Settings size={14} className="inline mr-2" />
                      Guardar Actual
                   </button>
               </div>

               <div>
                   <h4 className="font-bold text-[10px] opacity-50 mb-3 uppercase tracking-widest text-[#eab308]">Mis Presets</h4>
                   {loadingPresets ? (
                       <p className="text-center opacity-50 py-4 text-sm text-white">Cargando...</p>
                   ) : presets.length === 0 ? (
                       <p className="text-center opacity-50 py-4 text-sm text-white">No hay presets creados</p>
                   ) : (
                       <div className="space-y-2">
                           {presets.map(preset => (
                               <div key={preset.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col gap-3">
                                  <div className="flex justify-between items-start">
                                     <div>
                                        <h5 className="font-bold text-sm text-white">{preset.presetName}</h5>
                                        <span className="text-[10px] opacity-50 block mt-0.5 text-white">{new Date(preset.createdAt).toLocaleDateString()}</span>
                                     </div>
                                      <button 
                                        onClick={(e) => { e.preventDefault(); handleDeletePreset(preset.id); }}
                                        className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-lg transition-colors cursor-pointer relative z-10"
                                        title="Eliminar"
                                      >
                                          <X size={14} />
                                      </button>
                                  </div>
                                  <button 
                                    onClick={() => handleApplyPreset(preset)}
                                    className="w-full border border-white/10 bg-white/5 hover:bg-white/10 text-white font-bold px-3 py-1.5 rounded-lg transition-colors text-xs"
                                  >
                                      Aplicar Preset
                                  </button>
                                </div>
                           ))}
                       </div>
                   )}
               </div>
            </div>
          </div>
        </div>
      )}
    </div>



      <form
        onSubmit={handleCreate}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-black/20 p-6 rounded-2xl border border-white/10"
      >
        <div className="lg:col-span-4 mb-2">
          <h3 className="text-xl font-black uppercase tracking-wider flex items-center theme-font-secondary">
            <Star className="mr-2 theme-primary-text" /> Registrar Sponsor
          </h3>
          <p className="text-xs opacity-60">
            Añade las marcas o patrocinadores que soportan tu liga. Aparecerán de manera destacada en la portada principal de la plataforma oficial.
          </p>
        </div>

        <div className="lg:col-span-1 relative flex flex-col items-center justify-center bg-black/40 border border-white/10 rounded-xl p-4 overflow-hidden group cursor-pointer hover:theme-primary-border h-32">
          <input type="file" accept="image/png, image/webp" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer z-10 text-white placeholder-white/30" />
          {isLoading ? (
            <span className="text-xs theme-primary-text animate-pulse">Cargando...</span>
          ) : photoUrl ? (
            <img
              src={photoUrl}
              alt="Preview"
              className="h-full w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="flex flex-col items-center opacity-50">
              <Camera size={24} />
              <span className="text-[10px] mt-2 font-bold uppercase text-center">Logo<br/>(PNG/WebP)</span>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 space-y-4 flex flex-col justify-center">
          <input
            required
            type="text"
            placeholder="Nombre del Sponsor"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border font-bold text-lg"
          />

          <div className="animate-in fade-in slide-in-from-top-2 space-y-2">
            <input
              type="url"
              placeholder="Enlace o Hipervínculo (Ej: https://...)"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border text-sm"
            />
            <input
              type="text"
              placeholder="Instagram (Ej: @sponsor_oficial)"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border text-sm"
            />
            <input
              type="text"
              placeholder="WhatsApp (Ej: +5491112345678)"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border text-sm"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="lg:col-span-4 theme-primary-bg text-black font-black rounded-xl p-4 flex items-center justify-center transition-colors uppercase tracking-widest hover:opacity-80 mt-2"
        >
          <Plus size={20} className="mr-2" /> Añadir Sponsor
        </button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sponsors.map((c) => (
          <div key={c.id} className="p-5 rounded-2xl border border-white/10 bg-black/40 relative group flex gap-4 items-center">
            {editingId === c.id ? (
              <div className="w-full space-y-3">
                <div className="flex gap-4 items-center">
                  <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center p-2 shrink-0 border border-white/10 overflow-hidden relative group/img cursor-pointer">
                    <input type="file" accept="image/png, image/webp" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer z-10 text-white placeholder-white/30" />
                    {isLoading ? (
                      <span className="text-[10px] theme-primary-text animate-pulse">...</span>
                    ) : photoUrl ? (
                      <img src={photoUrl} alt="Preview" className="w-full h-full object-contain" />
                    ) : c.photoUrl ? (
                      <img src={c.photoUrl} alt={c.name} className="w-full h-full object-contain group-hover/img:opacity-50 transition-opacity" />
                    ) : (
                      <Camera className="opacity-30" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      className="w-full bg-black/60 border border-white/10 rounded p-2 text-sm focus:theme-primary-border outline-none font-bold text-white placeholder-white/30"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nombre del Sponsor"
                    />
                    <input
                      type="url"
                      className="w-full bg-black/60 border border-white/10 rounded p-2 text-xs focus:theme-primary-border outline-none text-white placeholder-white/30"
                      value={link}
                      onChange={(e) => setLink(e.target.value)}
                      placeholder="https://"
                    />
                    <input
                      type="text"
                      className="w-full bg-black/60 border border-white/10 rounded p-2 text-xs focus:theme-primary-border outline-none text-white placeholder-white/30"
                      value={instagram}
                      onChange={(e) => setInstagram(e.target.value)}
                      placeholder="Instagram (@)"
                    />
                    <input
                      type="text"
                      className="w-full bg-black/60 border border-white/10 rounded p-2 text-xs focus:theme-primary-border outline-none text-white placeholder-white/30"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                      placeholder="WhatsApp (+54...)"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button 
                    onClick={() => {
                       setEditingId(null);
                       setName('');
                       setLink('');
                       setInstagram('');
                       setWhatsapp('');
                       setPhotoUrl('');
                    }} 
                    className="px-3 py-1.5 text-xs font-bold bg-white/5 hover:bg-white/10 rounded-lg transition-colors border border-white/10 uppercase"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={async () => {
                      if (!name) return;
                      setIsLoading(true);
                      try {
                        let finalPhotoUrl = photoUrl || c.photoUrl;
                        if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image/')) {
                          const imageRef = ref(storage, `sponsors/sponsor_${c.id}_${Date.now()}.jpg`);
                          await uploadString(imageRef, finalPhotoUrl, 'data_url');
                          finalPhotoUrl = await getDownloadURL(imageRef);
                        }
                        
                        await setDoc(doc(db, getCollectionPath('clubs'), c.id), {
                          name,
                          link,
                          instagram,
                          whatsapp,
                          photoUrl: finalPhotoUrl,
                          type: 'sponsor',
                          createdAt: c.createdAt || Date.now(),
                        });
                        setEditingId(null);
                        setName('');
                        setLink('');
                        setInstagram('');
                        setWhatsapp('');
                        setPhotoUrl('');
                      } catch (err) {
                        console.error("Error updating sponsor:", err);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-black theme-primary-bg text-black rounded-lg transition-colors border border-transparent uppercase"
                  >
                    Guardar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center p-2 shrink-0 border border-white/10 overflow-hidden cursor-pointer hover:border-white/30" onClick={() => {
                   setEditingId(c.id);
                   setName(c.name);
                   setLink(c.link || '');
                   setInstagram(c.instagram || '');
                   setWhatsapp(c.whatsapp || '');
                   setPhotoUrl(c.photoUrl || '');
                }}>
                  {c.photoUrl ? <img src={c.photoUrl} alt={c.name} className="w-full h-full object-contain" /> : <Star className="opacity-30" />}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => {
                   setEditingId(c.id);
                   setName(c.name);
                   setLink(c.link || '');
                   setInstagram(c.instagram || '');
                   setWhatsapp(c.whatsapp || '');
                   setPhotoUrl(c.photoUrl || '');
                }}>
                  <h4 className="font-black text-lg truncate theme-font-secondary flex items-center hover:theme-primary-text transition-colors">{c.name}</h4>
                  <span className="text-[10px] px-2 py-0.5 rounded font-bold mt-1 inline-block bg-amber-500/20 text-amber-400 border border-amber-500/30">
                    Sponsor Oficial
                  </span>

                  {c.link && (
                    <div className="text-xs opacity-60 mt-2">
                      <a href={c.link} target="_blank" rel="noopener noreferrer" className="truncate flex items-center hover:theme-primary-text transition-colors" onClick={e => e.stopPropagation()}>
                        <LinkIcon size={10} className="mr-1 shrink-0" /> {c.link.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => deleteDoc(doc(db, getCollectionPath('clubs'), c.id))}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 p-2 bg-red-500/10 rounded-lg hover:text-red-300"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
